import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useTasks, useCompleteTask } from "./use-tasks";
import type { ManualTask } from "./types";

// A UI filter — not a task status. "Overdue" is a date filter (pending + past due),
// resolved server-side via ?overdue=true.
type TaskFilter = "PENDING" | "OVERDUE" | "COMPLETED";
const FILTERS: { value: TaskFilter; label: string }[] = [
  { value: "PENDING", label: "Pending" },
  { value: "OVERDUE", label: "Overdue" },
  { value: "COMPLETED", label: "Completed" },
];

const GRID = "grid-cols-[3fr_1.4fr_1.2fr_1fr_1fr]";

const isOverdue = (t: ManualTask) => t.status === "PENDING" && new Date(t.dueAt).getTime() < Date.now();
const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** Pending tasks for the current user (a REP sees only their own; server-enforced). */
export function TasksPage() {
  const [filter, setFilter] = useState<TaskFilter>("PENDING");
  // OVERDUE is a date filter (pending + past due), not a status.
  const query = filter === "OVERDUE" ? { overdue: true } : { status: filter };
  const { data, isLoading, isError, refetch } = useTasks(query);
  const complete = useCompleteTask();
  const rows = data?.items ?? [];

  const Tabs = (
    <div className="flex gap-2">
      {FILTERS.map((f) => (
        <Button
          key={f.value}
          variant={filter === f.value ? "primary" : "outline"}
          onClick={() => setFilter(f.value)}
          data-testid={`tasks-tab-${f.value.toLowerCase()}`}
        >
          {f.label}
        </Button>
      ))}
    </div>
  );

  const emptyMessage =
    filter === "PENDING"
      ? "No pending tasks. Nice, you're all caught up."
      : filter === "OVERDUE"
        ? "No overdue tasks."
        : "No completed tasks yet.";

  if (isError) {
    return (
      <div>
        <PageHeader title="Tasks" />
        <EmptyState
          message="Couldn't load tasks."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Tasks" footnote="Manual steps assigned to you from outreach sequences — call, review, follow up." />
      <div className="mb-3">{Tabs}</div>

      {!isLoading && rows.length === 0 ? (
        <EmptyState message={emptyMessage} />
      ) : (
        <TableCard>
          <TableHeadRow grid={GRID}>
            <Th>Task</Th>
            <Th>Due</Th>
            <Th>Status</Th>
            <Th>Type</Th>
            <Th> </Th>
          </TableHeadRow>
          {rows.map((t) => {
            const overdue = isOverdue(t);
            return (
              <TableRow key={t.id} grid={GRID} data-testid={`task-row-${t.id}`}>
                <div>
                  <Link to={`/tasks/${t.id}`} className="font-sans text-[13.5px] font-semibold text-ge-primary hover:text-ge-accent">
                    {t.title}
                  </Link>
                </div>
                <div className={overdue ? "font-mono text-[11.5px] font-semibold text-red-600" : "font-mono text-[11.5px] text-ge-value"} data-testid={overdue ? "task-overdue" : undefined}>
                  {fmt(t.dueAt)}
                  {overdue ? " · overdue" : ""}
                </div>
                <div>
                  <StatusText tone={t.status === "COMPLETED" ? "accent" : "faint"}>{t.status}</StatusText>
                </div>
                <div className="font-mono text-[11.5px] text-ge-secondary">{t.isBlocker ? "Blocking" : "Non-blocking"}</div>
                <div className="text-right">
                  {t.status === "PENDING" ? (
                    <Button
                      variant="outline"
                      data-testid={`task-complete-${t.id}`}
                      disabled={complete.isPending}
                      onClick={() => complete.mutate(t.id)}
                    >
                      Complete
                    </Button>
                  ) : null}
                </div>
              </TableRow>
            );
          })}
        </TableCard>
      )}
    </div>
  );
}
